/**
 * 编辑页 @ 候选聚合（单接口扁平列表，按 genType/videoMode 服务端硬过滤）。
 * 画布请使用 useCanvasReferenceableResources，不要复用本 hook。
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { aiConversationApi, type EditorMentionGenType } from '@/services/api/ai-conversations';
import { mapEditorMentionCandidatesToMentionResources } from '@/lib/conversations/editor-mention-candidates';
import { useOrgContextStore } from '@/stores/orgContextStore';

import type { MentionCandidateScope } from '@/types/mention';

const EDITOR_MENTION_QUERY_KEY = 'ai-editor-mention-candidates' as const;
const PERSONAL_CONTEXT_QUERY_KEY = '__personal__';

export const editorMentionKeys = {
  all: [EDITOR_MENTION_QUERY_KEY] as const,
  list: (
    activeOrgId: string | null,
    storyboardId: string,
    genType: EditorMentionGenType,
    videoMode?: string | null,
    scope?: MentionCandidateScope | null
  ) =>
    [
      ...editorMentionKeys.all,
      storyboardId,
      activeOrgId ?? PERSONAL_CONTEXT_QUERY_KEY,
      genType,
      videoMode ?? '',
      // scope 必须进 key：first_frame_audio 等模式下 @ 面板（音频）与帧 picker（图片）
      // 用同一组 genType/videoMode，缺了 scope 两者会串同一份缓存。
      scope ?? 'mention',
    ] as const,
};

export { mapEditorMentionCandidatesToMentionResources } from '@/lib/conversations/editor-mention-candidates';

export function useEditorMentionCandidates(
  storyboardId: string | null | undefined,
  params: {
    genType: EditorMentionGenType;
    videoMode?: string | null;
    /** 缺省按 @ 面板候选解析；帧 picker 传 'frame' */
    scope?: MentionCandidateScope | null;
  },
  options?: { enabled?: boolean }
) {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const enabled =
    Boolean(storyboardId) &&
    (options?.enabled ?? true) &&
    (params.genType !== 'video' || Boolean(params.videoMode));

  return useQuery({
    queryKey: editorMentionKeys.list(
      activeOrgId,
      storyboardId ?? '',
      params.genType,
      params.videoMode,
      params.scope
    ),
    queryFn: async () => {
      const response = await aiConversationApi.getEditorMentionCandidates(
        storyboardId!,
        {
          genType: params.genType,
          videoMode: params.videoMode,
          scope: params.scope,
        },
        { activeOrgId }
      );
      return mapEditorMentionCandidatesToMentionResources(response.resources ?? []);
    },
    enabled,
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 10,
    placeholderData: keepPreviousData,
  });
}
